import React from "react";
import { Mic, Square, Loader2, Sparkles, Volume2, Globe, ChevronDown, Cpu, Cloud, Zap } from "lucide-react";
import { LANGUAGES } from "../../lib/useJarvisVoice";
import Waveform from "./Waveform";

export default function JarvisVoicePanel({
  supported,
  phase,
  language,
  setLanguage,
  userCaption,
  aiCaption,
  isInterim,
  audioLevel,
  analyser,
  onMicClick,
  compact = false,
  errorMsg = "",
  voices = [],
  modelUsed = "",
  selectedVoiceName = "",
  preferredVoiceName = "",
  setPreferredVoiceName = () => {},
}) {
  // Derive service indicator from modelUsed
  const getServiceInfo = () => {
    if (!modelUsed) return { label: "Standby", icon: Cpu, color: "text-zinc-500" };
    const m = modelUsed.toLowerCase();
    if (m.includes("gemini")) return { label: "Netra Intelligence", icon: Cloud, color: "text-cyan-400" };
    if (m.includes("qwen") || m.includes("ollama") || m.includes("llama")) return { label: "Netra Core", icon: Cpu, color: "text-amber-400" };
    if (m === "fallback") return { label: "Offline", icon: Zap, color: "text-zinc-500" };
    return { label: "Neural Link", icon: Zap, color: "text-emerald-400" };
  };
  const serviceInfo = getServiceInfo();
  if (!supported) {
    return (
      <div className="rounded-2xl border border-ink-border-subtle bg-ink-surface/70 backdrop-blur p-5 text-sm text-zinc-400">
        Voice interface not available. Please use Chrome or Edge for the full experience.
      </div>
    );
  }

  const langMeta = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];

  const phaseConfig = {
    idle: {
      label: "Tap to ask me anything",
      color: "text-zinc-300",
      sublabel: "I'm ready to help, just speak your mind"
    },
    listening: {
      label: "I'm listening...",
      color: "text-cyan-400",
      sublabel: "Take your time, I'll catch every word"
    },
    thinking: {
      label: "Let me think about that...",
      color: "text-amber-400",
      sublabel: "Cross-referencing sources, one moment"
    },
    speaking: {
      label: "Here's what I found",
      color: "text-emerald-400",
      sublabel: "N.E.T.R.A is speaking"
    },
    introducing: {
      label: "Waking up...",
      color: "text-brand",
      sublabel: "Your truth companion is getting ready"
    },
  }[phase];

  const waveColor = {
    idle: "#52525b",
    listening: "#22d3ee",
    thinking: "#fbbf24",
    speaking: "#34d399",
    introducing: "#f59e0b",
  }[phase];

  const showUserCaption = userCaption && (phase === "listening" || phase === "thinking" || phase === "speaking");
  const showAiCaption = aiCaption && (phase === "speaking" || phase === "thinking" || phase === "idle");

  return (
    <div className={`relative rounded-2xl border bg-black/80 backdrop-blur-2xl shadow-2xl transition-all duration-500 overflow-hidden ${compact ? "p-3" : "p-5"
      } ${phase === "listening" ? "border-cyan-500/40 shadow-cyan-900/30" :
        phase === "thinking" ? "border-amber-500/40 shadow-amber-900/30" :
          phase === "speaking" ? "border-emerald-500/40 shadow-emerald-900/30" :
            phase === "introducing" ? "border-brand/40 shadow-brand/20" :
              "border-ink-border-subtle shadow-black/60"
      }`}>
      {/* Top status bar with glowing line */}
      <div className="absolute inset-x-0 top-0 h-0.5">
        <div
          className="h-full transition-all duration-500"
          style={{
            backgroundColor: waveColor,
            boxShadow: phase === "listening" ? `0 0 10px ${waveColor}` : "none",
            opacity: phase === "idle" ? 0.3 : 1
          }}
        />
      </div>

      {/* Header: Status + Language */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${phase === "listening" ? "bg-cyan-400 animate-pulse" :
              phase === "thinking" ? "bg-amber-400 animate-pulse" :
                phase === "speaking" ? "bg-emerald-400" :
                  phase === "introducing" ? "bg-brand animate-pulse" :
                    "bg-zinc-600"
            }`} />
          <span className={`text-xs font-medium ${phaseConfig.color}`}>
            {phaseConfig.label}
          </span>
        </div>

        <LangPicker
          language={language}
          onChange={setLanguage}
          disabled={phase !== "idle" && phase !== "introducing"}
        />
      </div>

      {errorMsg && (
        <div className="mb-3 p-3 text-xs bg-red-950/40 border border-red-900/50 text-red-400 rounded-lg animate-in fade-in slide-in-from-top-2">
          {errorMsg}
        </div>
      )}

      {/* Main Caption Area - Jarvis Style */}
      <div className="min-h-[100px] max-h-[300px] overflow-y-auto custom-scrollbar space-y-3 pr-2">
        {/* User Caption (Real-time) */}
        {showUserCaption && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-center gap-2 mb-1">
              <Mic size={12} className="text-cyan-400" />
              <span className="text-[10px] uppercase tracking-wider text-cyan-400/70">
                You {isInterim && "(typing...)"}
              </span>
            </div>
            <div className="text-zinc-200 text-sm leading-relaxed pl-4 border-l-2 border-cyan-400/30">
              {userCaption}
              {isInterim && <span className="animate-pulse">|</span>}
            </div>
          </div>
        )}

        {/* AI Caption (Jarvis Response) */}
        {showAiCaption && (
          <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles size={12} className="text-brand" />
              <span className="text-[10px] uppercase tracking-wider text-brand/70">
                N.E.T.R.A
              </span>
            </div>
            <div className={`text-zinc-100 text-sm leading-relaxed pl-4 border-l-2 transition-all duration-300 ${phase === "speaking" ? "border-emerald-400/50 font-medium" : "border-brand/30"
              }`}>
              {aiCaption}
              {phase === "speaking" && (
                <span className="inline-flex items-center ml-2">
                  <Volume2 size={14} className="text-emerald-400 animate-pulse" />
                </span>
              )}
            </div>
          </div>
        )}

        {/* Empty State - Warm Welcome */}
        {!showUserCaption && !showAiCaption && (
          <div className="text-center py-4">
            <div className="inline-flex items-center gap-2 text-brand/80 text-xs uppercase tracking-widest mb-2">
              <Sparkles size={12} />
              N.E.T.R.A
            </div>
            <p className="text-zinc-300 text-sm leading-relaxed">
              Speak a headline, paste a claim, or just ask “is this real?”
            </p>
            <p className="text-zinc-500 text-xs mt-1">
              I'll give you the truth, no jargon, no guessing.
            </p>
          </div>
        )}
      </div>

      {/* Waveform Visualization */}
      <div className="mt-4">
        <Waveform
          level={audioLevel}
          analyser={analyser}
          active={phase === "listening"}
          color={waveColor}
          height={compact ? 30 : 40}
        />
      </div>

      {/* Bottom Controls */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex flex-col gap-0.5">
          <div className="text-xs text-zinc-500">{phaseConfig.sublabel}</div>
          <div className="text-[10px] text-zinc-600 flex items-center gap-1.5 mt-1">
              {React.createElement(serviceInfo.icon, { size: 10, className: serviceInfo.color })}
              <span className={`${serviceInfo.color} font-medium tracking-wide`}>
                {serviceInfo.label}
              </span>
            </div>
        </div>

        <button
          onClick={onMicClick}
          disabled={phase === "thinking" || phase === "introducing"}
          className={`relative inline-flex items-center justify-center w-14 h-14 rounded-full transition-all duration-300 ${phase === "listening"
              ? "bg-cyan-500/20 border-2 border-cyan-400 shadow-lg shadow-cyan-500/30 animate-pulse"
              : phase === "thinking"
                ? "bg-amber-500/20 border-2 border-amber-400 cursor-wait"
                : phase === "speaking"
                  ? "bg-emerald-500/20 border-2 border-emerald-400"
                  : "bg-zinc-800 hover:bg-zinc-700 border-2 border-zinc-600 hover:border-zinc-500"
            }`}
        >
          {phase === "listening" ? (
            <Square size={20} className="text-cyan-400 fill-current" />
          ) : phase === "thinking" ? (
            <Loader2 size={24} className="text-amber-400 animate-spin" />
          ) : phase === "speaking" ? (
            <Volume2 size={24} className="text-emerald-400" />
          ) : (
            <Mic size={24} className="text-zinc-300" />
          )}

          {/* Ripple effect when listening */}
          {phase === "listening" && (
            <>
              <span className="absolute inset-0 rounded-full border border-cyan-400/50 animate-ping" />
              <span className="absolute -inset-2 rounded-full border border-cyan-400/30 animate-ping delay-75" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function LangPicker({ language, onChange, disabled }) {
  const [open, setOpen] = React.useState(false);
  const meta = LANGUAGES.find((l) => l.code === language) || LANGUAGES[0];
  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setOpen((s) => !s)}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border border-ink-border-subtle bg-ink-elevated/50 transition-colors ${disabled ? "text-zinc-600 cursor-not-allowed" : "text-zinc-300 hover:border-zinc-500 hover:text-white"
          }`}
      >
        <Globe size={12} className={disabled ? "text-zinc-600" : "text-zinc-400"} />
        {meta.label}
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-40 w-40 bg-ink-elevated border border-ink-border rounded-xl shadow-2xl overflow-hidden max-h-72 overflow-y-auto">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => { onChange(l.code); setOpen(false); }}
                className={`w-full text-left px-4 py-2 hover:bg-ink-soft text-sm transition-colors flex items-center justify-between ${l.code === language ? "bg-ink-soft text-white" : "text-zinc-300"
                  }`}
              >
                <span>{l.label}</span>
                {l.code === language && <span className="w-1.5 h-1.5 rounded-full bg-brand" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
